import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import Home from './Home';
import PrivacyPolicy from './PrivacyPolicy';
import {
  type Language,
  getInitialLanguage,
  saveLanguageToStorage
} from './utils/languageUtils';

interface Translations {
  en: {
    hero: {
      title: string;
      subtitle: string;
      downloadApp: string;
    };
    features: {
      title: string;
      items: Array<{
        title: string;
        description: string;
      }>;
    };
    howItWorks: {
      title: string;
      steps: Array<{
        title: string;
        description: string;
      }>;
    };
    footer: {
      madeWith: string;
    };
  };
  uk: {
    hero: {
      title: string;
      subtitle: string;
      downloadApp: string;
    };
    features: {
      title: string;
      items: Array<{
        title: string;
        description: string;
      }>;
    };
    howItWorks: {
      title: string;
      steps: Array<{
        title: string;
        description: string;
      }>;
    };
    footer: {
      madeWith: string;
    };
  };
}

const translations: Translations = {
  en: {
    hero: {
      title: 'MediSnap',
      subtitle: 'Smart prescription management with AI-powered scanning',
      downloadApp: 'Download Now',
    },
    features: {
      title: 'Features',
      items: [
        {
          title: 'AI-Powered Scanning',
          description: 'Scan your prescription or doctor\'s notes with your camera. MediSnap automatically extracts medication names, dosages, and schedules.',
        },
        {
          title: 'Smart Reminders',
          description: 'Never miss a dose. Get timely notifications for each medication. Schedule can be planned for future dates and synced with your device calendar.',
        },
        {
          title: 'Manual Entry',
          description: 'Add medications manually with flexible scheduling options. Full control over your medication management.',
        },
        {
          title: 'Well-being Tracking',
          description: 'Track your daily well-being with notes. Get AI-generated summaries of your health journey over time.',
        },
        {
          title: 'Secure & Private',
          description: 'Protect your data with PIN code or Face ID. Your health information stays private and secure on your device.',
        },
        {
          title: 'Light & Dark Theme',
          description: 'Choose your preferred theme for comfortable viewing any time of day.',
        },
      ],
    },
    howItWorks: {
      title: 'How It Works',
      steps: [
        {
          title: 'Scan or Enter',
          description: 'Take a photo of your prescription, upload an image, or enter medication details manually.',
        },
        {
          title: 'Review & Confirm',
          description: 'MediSnap extracts the information. Review and edit if needed.',
        },
        {
          title: 'Get Reminders',
          description: 'Receive notifications at the right time. Track your medication adherence with ease.',
        },
      ],
    },
    footer: {
      madeWith: 'Made with care for your health',
    },
  },
  uk: {
    hero: {
      title: 'MediSnap',
      subtitle: 'Розумне керування ліками з AI-скануванням',
      downloadApp: 'Завантажити',
    },
    features: {
      title: 'Можливості',
      items: [
        {
          title: 'AI-сканування',
          description: 'Скануйте рецепт або записи лікаря камерою. MediSnap автоматично розпізнає назви ліків, дозування та розклад прийому.',
        },
        {
          title: 'Розумні нагадування',
          description: 'Ніколи не пропускайте прийом. Отримуйте своєчасні сповіщення для кожного препарату. Розклад можна планувати на майбутнє та синхронізувати з календарем.',
        },
        {
          title: 'Ручне додавання',
          description: 'Додавайте ліки вручну з гнучкими налаштуваннями розкладу. Повний контроль над керуванням медикаментами.',
        },
        {
          title: 'Відстеження самопочуття',
          description: 'Записуйте щоденні нотатки про самопочуття. Отримуйте AI-згенеровані підсумки вашого здоров\'я за період.',
        },
        {
          title: 'Безпека і конфіденційність',
          description: 'Захистіть дані PIN-кодом або Face ID. Ваша медична інформація залишається приватною та безпечною на вашому пристрої.',
        },
        {
          title: 'Світла і темна теми',
          description: 'Оберіть зручну тему для комфортного перегляду в будь-який час доби.',
        },
      ],
    },
    howItWorks: {
      title: 'Як це працює',
      steps: [
        {
          title: 'Скануйте або введіть',
          description: 'Сфотографуйте рецепт, завантажте зображення або введіть дані про ліки вручну.',
        },
        {
          title: 'Перевірте та підтвердьте',
          description: 'MediSnap витягне інформацію. Перегляньте та відредагуйте за потреби.',
        },
        {
          title: 'Отримуйте нагадування',
          description: 'Отримуйте сповіщення в потрібний час. Легко відстежуйте дотримання графіка прийому ліків.',
        },
      ],
    },
    footer: {
      madeWith: 'Створено з турботою про ваше здоров\'я',
    },
  },
};

function App() {
  const [language, setLanguage] = useState<Language>(() => getInitialLanguage());

  // Save language to localStorage whenever it changes
  useEffect(() => {
    saveLanguageToStorage(language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'uk' : 'en');
  };

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route
            path="/"
            element={<Home language={language} toggleLanguage={toggleLanguage} translations={translations} />}
          />
          <Route
            path="/privacy"
            element={
              <>
                <button className="language-toggle" onClick={toggleLanguage} aria-label="Toggle language">
                  {language === 'en' ? '🇺🇦 UA' : '🇬🇧 EN'}
                </button>
                <PrivacyPolicy language={language} />
              </>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
